/**
 * 牌桌渲染模型的派生函数（§11.3）。
 *
 * 单独成文件是为了能在 node 环境直接单测：这里零 React、零 CSS、零 DOM，
 * 只把 `PublicHandState` 折算成「座位块要显示什么」。
 *
 * ★ 最重要的一条：`bestFiveSet` 的防御性裁剪。服务端已经裁过一次牌面，
 * 但 `HandRank.bestFive` 里装的是**真实 CardId**——只要有一处忘了过滤，
 * 「全员弃牌、赢家不亮牌」这一手就会把赢家的底牌直接画上屏幕。
 * §11.3 明确要求客户端再做一道，这里就是那道。
 */

import type { CardId, Chips, PublicHandState, SeatIndex } from '@poker/shared'

import { actionAmountOf, actionKindOf, handLabelShort, type SeatLastAction } from './cardFormat.js'

/** 座位元信息（与 tableStore.TableSeatInfo 同构，避免本文件依赖 store） */
export interface SeatLike {
  readonly seat: SeatIndex
}

export function indexBySeat<T extends SeatLike>(items: readonly T[]): Map<SeatIndex, T> {
  const m = new Map<SeatIndex, T>()
  for (const s of items) m.set(s.seat, s)
  return m
}

/** 本街尚未收进底池的投入总额 */
export function sumStreetCommitted(view: PublicHandState | null): Chips {
  if (view === null) return 0
  let t = 0
  for (const p of Object.values(view.players)) t += p.committedThisRound
  return t
}

/**
 * 底池显示值。引擎的 `potTotal` 恒为 Σ committedThisHand（含本街已下注），
 * 直接显示会和桌面上摆着的下注筹码重复计数。
 */
export function displayedPot(view: PublicHandState | null): Chips {
  if (view === null) return 0
  return Math.max(0, view.potTotal - sumStreetCommitted(view))
}

/**
 * 最佳五张高亮集合，带防御性裁剪：
 * 只保留「已发出的公共牌」或「已亮牌座位的底牌」，其余一律剔除。
 * `endedBy === 'fold'` 时引擎保证 `showdown` 为空数组，于是这里自然返回 null。
 */
export function bestFiveSet(view: PublicHandState | null): ReadonlySet<CardId> | null {
  if (view === null || view.phase !== 'complete') return null
  const result = view.result
  if (result === null || result.endedBy !== 'showdown') return null

  const visible = new Set<CardId>(view.board)
  for (const e of result.showdown) {
    if (e.mucked) continue
    visible.add(e.holeCards[0])
    visible.add(e.holeCards[1])
  }

  const out = new Set<CardId>()
  for (const e of result.showdown) {
    if (e.mucked) continue
    for (const c of e.rank.bestFive) if (visible.has(c)) out.add(c)
  }
  return out.size === 0 ? null : out
}

/** 每座位在**当前街**的最后一个行动，供行动气泡使用（换街即清空） */
export function lastActionsBySeat(view: PublicHandState | null): Map<SeatIndex, SeatLastAction> {
  const out = new Map<SeatIndex, SeatLastAction>()
  if (view === null || view.street === null) return out
  for (const a of view.actionLog) {
    if (a.street !== view.street) continue
    const base = actionKindOf(a.type, { seat: a.seat, bigBlindSeat: view.bigBlindSeat })
    // 补盲（哪怕筹码不够、补着补着全下了）一律并进「补盲 N」：说「全下」看不出他是在补盲
    if (base === 'postEntry' || base === 'postDeadBlind') {
      // 补盲的活钱和补的小盲合成一个气泡：「补盲 30」
      const prev = out.get(a.seat)
      out.set(a.seat, { kind: 'postEntry', amount: (prev?.kind === 'postEntry' ? prev.amount : 0) + a.delta })
      continue
    }
    // allIn 不是引擎的动作类型（会被归一成 call/bet/raise），但气泡必须单独喊出来
    out.set(a.seat, { kind: a.allIn ? 'allIn' : base, amount: actionAmountOf(a) })
  }
  return out
}

/** 摊牌后的牌型文案。一律走 formatHandShort（@poker/shared），绝不在 UI 层拼字符串。 */
export function handLabelsBySeat(view: PublicHandState | null): Map<SeatIndex, string> {
  const out = new Map<SeatIndex, string>()
  const result = view?.result
  if (result == null || result.endedBy !== 'showdown') return out
  for (const e of result.showdown) {
    if (e.mucked) continue
    out.set(e.seat, handLabelShort(e.rank))
  }
  return out
}

/** 赢家座位（amount > 0 才算，0 额度的 award 只是边池占位） */
export function winnerSeats(view: PublicHandState | null): ReadonlySet<SeatIndex> {
  const out = new Set<SeatIndex>()
  for (const a of view?.result?.awards ?? []) if (a.amount > 0) out.add(a.seat)
  return out
}

/**
 * 本手是否已经不需要再显示「行动中」高亮。
 * 用 `phase === 'complete'` 而不是 `actionOn === null`：
 * 街与街之间 actionOn 也会短暂为 null，那时高亮该保留在最后行动者身上。
 */
export function isHandOver(view: PublicHandState | null): boolean {
  return view === null || view.phase === 'complete'
}
